import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import api from '../axios/axiosConfig';
import { isWebView } from '../utils/detectWebContainer';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export type LocationContextType = {
  userLocation: any;
  searchLocation: { lat: number; lng: number } | null | undefined;
  searchRadius: number;
  standardLocation: google.maps.LatLng;
  isGpsLocation: boolean | null;
  setUserLocation: (coords: { lat: number; lng: number }) => void;
  setSearchLocation: (coords: { lat: number; lng: number } | null) => void;
  setSearchRadius: (radius: number) => void;
  refreshCurrentPosition: () => void;
  getUserDistance: (
    toPoint: { lat: number; lng: number },
    targetUnit: keyof typeof distanceConversion,
    decimalPlaces: number
  ) => number;
  getDistance: (
    { from, to }: UseDistanceTypes,
    targetUnit: keyof typeof distanceConversion,
    decimalPlaces: number
  ) => number;
};

const webView = isWebView();

const LocationContext = createContext<LocationContextType | undefined>(
  undefined
);

type LocationProviderProps = {
  children: ReactNode;
};

type UseDistanceTypes = {
  from: { lat: number | undefined; lng: number | undefined } | undefined;
  to: { lat: number | undefined; lng: number | undefined } | undefined;
};

const earthRadius = 6378137;
const toRadius = (value: number): number => (value * Math.PI) / 180;

const getDistance = (
  { from, to }: UseDistanceTypes,
  targetUnit: keyof typeof distanceConversion = 'm',
  decimalPlaces: number = 0
): number => {
  if (!from?.lat || !from?.lng || !to?.lat || !to?.lng) return -1;

  const distance =
    Math.acos(
      Math.sin(toRadius(to.lat)) * Math.sin(toRadius(from.lat)) +
        Math.cos(toRadius(to.lat)) *
          Math.cos(toRadius(from.lat)) *
          Math.cos(toRadius(from.lng) - toRadius(to.lng))
    ) * earthRadius;

  return parseFloat(
    convertDistance(distance, targetUnit).toFixed(decimalPlaces)
  );
};

// Convert to a different unit
const convertDistance = (
  meters: number,
  targetUnit: keyof typeof distanceConversion = 'm'
): number => {
  return distanceConversion[targetUnit] * meters;
};

const distanceConversion = {
  m: 1,
  mi: 1 / 1609.344,
  km: 0.001,
  cm: 100,
  mm: 1000,
};

const checkGPSPermission = async (): Promise<boolean> => {
  console.log('Check GPS permission');

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    if (result.state === 'denied') {
      console.error('GPS permission has been denied');
      return false;
    }
  } catch (error: any) {
    console.error('Error checking GPS permissions:', error);
    return false;
  }

  return true;
};

export const LocationProvider = ({ children }: LocationProviderProps) => {
  const standardLocation: any = {
    lat: -23.5616364,
    lng: -46.6561378,
  };
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  }>(standardLocation);
  const [isGpsLocation, setIsGpsLocation] = useState<boolean | null>(null);
  const [searchLocation, setSearchLocation] = useState<{
    lat: number;
    lng: number;
  } | null>();
  const [searchRadius, setSearchRadius] = useState<number>(10000);

  useEffect(() => {
    checkGPSPermission();
    refreshCurrentPosition();
  }, []);

  useEffect(() => {
    if (!searchLocation && userLocation) setSearchLocation(userLocation);
  }, [userLocation]);

  function getUserDistance(
    toPoint: { lat: number; lng: number },
    targetUnit: keyof typeof distanceConversion = 'km',
    decimalPlaces: number = 1
  ) {
    if (!userLocation) return -1;

    return getDistance(
      { from: userLocation, to: toPoint },
      targetUnit,
      decimalPlaces
    );
  }

  function refreshCurrentPosition() {
    const savedView: any = loadSavedView();

    if (webView) {
      const handler = (event: any) => {
        const { lat, lng } = event.detail || {};
        if (lat && lng) {
          console.log('Localização recebida do nativo (fallback):', lat, lng);

          setIsGpsLocation(true);
          api.defaults.headers.common['User-Location'] = `${lat};${lng};0`;
          const newLocation = { lat, lng };
          setUserLocation(newLocation);
          setSearchLocation(
            savedView?.center ? savedView?.center : newLocation
          );

          window.removeEventListener('nativeLocation', handler);
        } else {
          setIsGpsLocation(false);
          delete api.defaults.headers.common['User-Location'];
          setUserLocation(standardLocation);
          setSearchLocation(
            savedView?.center ? savedView?.center : standardLocation
          );
        }
      };
      window.addEventListener('nativeLocation', handler);
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ type: 'REQUEST_NATIVE_LOCATION' })
      );
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          if (coords) {
            setIsGpsLocation(true);
            api.defaults.headers.common[
              'User-Location'
            ] = `${coords.latitude};${coords.longitude};${coords.accuracy}`;
            const newLocation = {
              lat: coords.latitude,
              lng: coords.longitude,
            };
            setUserLocation(newLocation);
            setSearchLocation(
              savedView?.center ? savedView?.center : newLocation
            );
          }
        },
        (error) => {
          console.error('Error getting location: ', error);
          setIsGpsLocation(false);
          delete api.defaults.headers.common['User-Location'];
          setUserLocation(standardLocation);
          setSearchLocation(
            savedView?.center ? savedView?.center : standardLocation
          );
        }
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      setIsGpsLocation(false);
      delete api.defaults.headers.common['User-Location'];
      setUserLocation(standardLocation);
      setSearchLocation(
        savedView?.center ? savedView?.center : standardLocation
      );
    }
  }

  return (
    <LocationContext.Provider
      value={{
        userLocation,
        searchLocation,
        searchRadius,
        standardLocation,
        isGpsLocation,
        setUserLocation,
        setSearchLocation,
        setSearchRadius,
        refreshCurrentPosition,
        getUserDistance,
        getDistance,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within an LocationProvider');
  }
  return context;
};

const SESSION_KEYS = {
  center: 'map.center',
  zoom: 'map.zoom',
  bounds: 'map.bounds',
};

export const loadSavedView = (): {
  center?: { lat: number; lng: number };
  bounds?: google.maps.LatLngBoundsLiteral;
  zoom?: number;
} | null => {
  try {
    const rawCenter = sessionStorage.getItem(SESSION_KEYS.center);
    const rawBounds = sessionStorage.getItem(SESSION_KEYS.bounds);
    if (!rawCenter || !rawBounds) return null;
    const center = JSON.parse(rawCenter) as { lat: number; lng: number };
    const bounds = rawBounds
      ? (JSON.parse(rawBounds) as google.maps.LatLngBoundsLiteral)
      : undefined;
    const zoomStr = sessionStorage.getItem(SESSION_KEYS.zoom);
    const zoom = zoomStr ? Number(zoomStr) : undefined;
    if (Number.isFinite(center?.lat) && Number.isFinite(center?.lng)) {
      return { center, zoom, bounds };
    }
  } catch {}
  return null;
};

export const saveView = ({
  center,
  zoom,
  bounds,
}: {
  center?: { lat: number; lng: number };
  zoom?: number | null;
  bounds?: any;
}) => {
  try {
    sessionStorage.setItem(SESSION_KEYS.center, JSON.stringify(center));
    if (typeof zoom === 'number' && Number.isFinite(zoom)) {
      sessionStorage.setItem(SESSION_KEYS.zoom, String(zoom));
    }
    if (bounds) {
      sessionStorage.setItem(SESSION_KEYS.bounds, JSON.stringify(bounds));
    }
  } catch {}
};
